import { FC, useEffect, useState } from 'react';
import CreateEditLayout from '@features/workspace/components/layouts/sub/create-edit-layout';
import CustomerProfileEditCompanyDetails from '@features/workspace/views/company/edit-profile/company-details';
import CustomerProfileEditBusinessAddress from '@features/workspace/views/company/edit-profile/business-address';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectActiveCompany } from '@features/workspace/store/selectors';
import _ from 'lodash';
import history from '@app/services/history';
import LeaveFormSaveDialog from '@features/workspace/components/dialogs/leave-form-save-dialog';
import { INIT_DATA as INIT_ADDRESS_DATA } from '@features/workspace/components/users/components/address-fields';
import {
  updateCompany,
  updateCompanyAddress,
  createCompanyAddress,
} from '@features/workspace/store/actions';
import companyDetailsSchema from '@features/workspace/views/company/schemas/company-details-schema';
import { setIsLoading } from '@store/features/loading';
import { getPageById } from '@app/services/pages-meta';
import { setNotification } from '@store/features/notification';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { yearsOperationalEnumMap } from '@features/workspace/views/company/utils/company-management';
import { CompanyTypeEnum } from '@utils/constants';
import logger from '@/app/services/logger';

const INIT_DATA = {
  id: null,
  annualTurnover: '',
  businessBEEStatus: -1,
  businessRegistrationDate: '',
  companyType: CompanyTypeEnum,
  email: '',
  friendbuyInvitingAdvocateCode: '',
  industry: '',
  landlineNumber: '',
  name: '',
  phoneNumber: '',
  primaryUserId: '',
  registrationNumber: '',
  taxNumber: '',
  tradingAs: '',
  vatRegistrationNumber: '',
  website: '',
  yearsOperational: '',
  // Address
  address: INIT_ADDRESS_DATA,
};

const CustomerProfileEdit: FC<any> = () => {
  const company: any = useAppSelector(selectActiveCompany);
  const dispatch = useAppDispatch();
  const backUri = '/customer/company';
  const [openLeaveFormSaveDialog, setOpenLeaveFormSaveDialog] = useState(false);

  const methods = useForm({
    defaultValues: INIT_DATA,
    resolver: yupResolver(companyDetailsSchema),
  });

  const { register, handleSubmit, formState, reset, control, watch } = methods;

  const { errors, isDirty, dirtyFields } = formState;

  const formData = watch();

  const companyData = _.omit(formData, ['address']);
  const addressData = formData.address;

  useEffect(() => {
    logger.debug('Form data changed');
  }, [formData]);

  logger.debug('Form Errors', errors);

  useEffect(() => {
    if (company) {
      const rawYearsOperational = company.yearsOperational;
      let mappedYearsOperational = rawYearsOperational;

      if (
        rawYearsOperational !== null &&
        !Number.isNaN(Number(rawYearsOperational))
      ) {
        mappedYearsOperational =
          yearsOperationalEnumMap[Number(rawYearsOperational)] ||
          rawYearsOperational;
      }

      const initialState = {
        ...INIT_DATA,
        ..._.omitBy(company, _.isNil),
        address: {
          ...INIT_DATA.address,
          ..._.omitBy(company?.address || {}, _.isNil),
        },
      };

      initialState.yearsOperational = mappedYearsOperational;

      logger.debug('Init state', initialState);
      reset(initialState);
    }
  }, [company, reset]);

  const onSave = async (currentFormData: any) => {
    dispatch(setIsLoading(true));

    const formCompanyData = _.omit(currentFormData, ['address']);
    const formCompanyDirtyFieldNames = _.omit(dirtyFields, ['address']);
    const formCompanyDirtyData = Object.keys(formCompanyDirtyFieldNames).reduce(
      (acc: any, field: string) => {
        if (formCompanyData[field] !== undefined) {
          acc[field] =
            formCompanyData[field] !== null ? formCompanyData[field] : '';
        }

        return acc;
      },
      {}
    );
    // Drop empty registration number

    logger.debug('Form Company Dirty Data', formCompanyDirtyData);

    if (formCompanyDirtyData.businessRegistrationDate) {
      formCompanyDirtyData.businessRegistrationDate = moment.isMoment(
        formCompanyDirtyData.businessRegistrationDate
      )
        ? formCompanyDirtyData.businessRegistrationDate.format('YYYY-MM-DD')
        : moment(formCompanyDirtyData.businessRegistrationDate).format(
            'YYYY-MM-DD'
          );
    }

    // TODO: Next logic is temporary fix for Credit API validation problem when it expects 0000 as VAT if company has application but doesn't have VAT
    formCompanyDirtyData.companyType = companyData.companyType;
    formCompanyDirtyData.vatRegistrationNumber =
      formCompanyData.vatRegistrationNumber || null;
    formCompanyDirtyData.taxNumber = formCompanyData.taxNumber || null;
    formCompanyDirtyData.registrationNumber =
      formCompanyData.registrationNumber || null;

    // ADDRESS
    const formAddressData = currentFormData.address;
    const formAddressDirtyFieldNames = dirtyFields.address || {};
    const formAddressDirtyData = Object.keys(formAddressDirtyFieldNames).reduce(
      (acc: any, field: string) => {
        if (formAddressData[field] !== undefined) {
          acc[field] = formAddressData[field];
        }

        return acc;
      },
      {}
    );

    logger.debug('Form Address Dirty Data', formAddressDirtyData);

    try {
      if (formAddressData.id) {
        if (Object.keys(formAddressDirtyData).length) {
          await dispatch(
            updateCompanyAddress({
              companyId: company?.id,
              data: formAddressDirtyData,
            })
          ).unwrap();
        }
      } else {
        await dispatch(
          createCompanyAddress({
            companyId: company?.id,
            data: formAddressData,
          })
        ).unwrap();
      }

      if (Object.keys(formCompanyDirtyData).length) {
        await dispatch(
          updateCompany({
            companyId: companyData?.id,
            data: formCompanyDirtyData,
          })
        ).unwrap();
      }

      dispatch(
        setNotification({
          message: 'Company details updated',
          variant: 'success',
        })
      );
      goBack();
    } catch (err: any) {
      logger.error(err, err.message);
    } finally {
      dispatch(setIsLoading(false));
      setOpenLeaveFormSaveDialog(false);
    }
  };

  const goBack = () => {
    // Check if coming from documents listing page or not
    if (document.location.hash === '#documents') {
      history.push(getPageById('documents')?.uri);
    } else {
      history.push(backUri);
    }
  };

  const onGoBack = () => {
    if (isDirty) {
      setOpenLeaveFormSaveDialog(true);
    } else {
      goBack();
    }
  };

  const asTabs = [
    {
      title: 'Company details',
      content: (
        <CustomerProfileEditCompanyDetails
          control={control}
          data={companyData}
          errors={errors}
          register={register}
        />
      ),
    },
    {
      title: 'Business Address',
      content: (
        <CustomerProfileEditBusinessAddress
          control={control}
          data={addressData}
          errors={errors}
          register={register}
        />
      ),
    },
  ];

  return (
    <FormProvider {...methods}>
      <CreateEditLayout
        asTabs={asTabs}
        backUri={backUri}
        errors={errors}
        // title={'Edit company'}
        onBack={onGoBack}
        onCancel={onGoBack}
        onSave={handleSubmit(onSave)}
      />
      <LeaveFormSaveDialog
        open={openLeaveFormSaveDialog}
        onAccept={handleSubmit(onSave)}
        onClose={() => setOpenLeaveFormSaveDialog(false)}
        onReject={() => {
          goBack();
        }}
      />
    </FormProvider>
  );
};

export default CustomerProfileEdit;
